/**
 * Sentry plugin entrypoint for the frontend app.
 *
 * Responsibilities:
 * - initialize Sentry once with app-wide filters/config,
 * - expose auth user helpers (`setAuthUser` / `clearAuthUser`),
 * - register `$sentry` on Vue global properties.
 */
import type { App } from 'vue';
import * as Sentry from '@sentry/vue';
import { isEmpty } from 'lodash-es';
import { isChunkLoadError, isStaleChunkReloadPending } from '@/plugins/chunk-reload.ts';

type SentryAuthUser = {
    id: number | string;
    email: string;
    first_name: string;
    last_name: string;
};

type SentryClientType = {
    captureException: (error: unknown) => string;
    isInitialized: boolean;
    clearAuthUser: () => void;
    init: (app: App) => void;
    setAuthUser: (user: SentryAuthUser | null) => void;
};

const SentryClient: SentryClientType = {
    isInitialized: false,

    captureException(error: unknown): string {
        return Sentry.captureException(error);
    },

    init(app: App): void {
        if (this.isInitialized) {
            return;
        }

        Sentry.init({
            app,
            logErrors: true,
            dsn: process.env.VUE_APP_SENTRY_DSN,
            environment: process.env.VUE_APP_ENV,
            release: `activix-crm-frontend@${process.env.VUE_APP_VERSION}`,
            beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
                if (!process.env.VUE_APP_SENTRY_DSN) {
                    return null;
                }

                // A stale-chunk reload is already recovering this page, so the failed
                // import is expected. Only drop it once the reload is in flight — when the
                // loop guard blocks it, the chunk is genuinely missing and we want to know.
                if (isStaleChunkReloadPending() && isChunkLoadError(hint?.originalException)) {
                    return null;
                }

                // Drop Auth0 session-expiry errors (they throw objects, not Errors, so ignoreErrors can't catch them)
                const serialized = event.extra?.__serialized__;
                if (
                    serialized &&
                    typeof serialized === 'object' &&
                    'error_description' in serialized &&
                    ['timeout', 'login_required'].includes(
                        (serialized as Record<string, unknown>).code as string
                    )
                ) {
                    return null;
                }

                return event;
            },
            ignoreErrors: [
                'Request failed with status code 400',
                'Request failed with status code 401',
                'Request failed with status code 403',
                'Request failed with status code 404',
                'Request failed with status code 422',
                'Request failed with status code 500',
                'Network Error',
                'Request aborted',

                // Element UI
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications.',

                // Handy Scroll
                "Cannot read property 'scrollLeft' of null",

                // Custom
                'Non-Error promise rejection captured with value: undefined', // Caused by login redirect to Auth0

                // Common errors: https://gist.github.com/impressiver/5092952
                'top.GLOBALS',
                'originalCreateNotification',
                'canvas.contentDocument',
                'MyApp_RemoveAllHighlights',
                'http://tt.epicplay.com',
                "Can't find variable: ZiteReader",
                'jigsaw is not defined',
                'ComboSearch is not defined',
                'http://loading.retry.widdit.com/',
                'atomicFindClose',
                'fb_xd_fragment',
                'bmi_SafeAddOnload',
                'EBCallBackMessageReceived',
                'conduitPage',
                'Script error.',
            ],
            denyUrls: [
                // Common errors: https://gist.github.com/impressiver/5092952
                /graph\.facebook\.com/i,
                /connect\.facebook\.net\/en_US\/all\.js/i,
                /eatdifferent\.com\.woopra-ns\.com/i,
                /static\.woopra\.com\/js\/woopra\.js/i,
                /extensions\//i,
                /^chrome:\/\//i,
                /127\.0\.0\.1:4001\/isrunning/i,
                /webappstoolbarba\.texthelp\.com\//i,
                /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            ],
        });

        this.isInitialized = true;
    },

    clearAuthUser(): void {
        Sentry.setUser(null);
    },

    setAuthUser(user: SentryAuthUser | null): void {
        if (isEmpty(user) || !user) {
            this.clearAuthUser();
        } else {
            Sentry.setUser({
                id: user.id,
                email: user.email,
                name: `${user.first_name} ${user.last_name}`,
            });
        }
    },
};

const VueSentry = {
    installed: false,

    install(app: App): void {
        if (this.installed) {
            return;
        }

        this.installed = true;
        SentryClient.init(app);
        app.config.globalProperties.$sentry = SentryClient;
    },
};

export type { SentryAuthUser };
export { VueSentry };
export default SentryClient;
